import { ALARM_TYPES, buildSiteTree, localDayRange, type Alarm, type SiteNode } from "@fts/core";
import { useEffect, useMemo, useState } from "react";
import { PageHeader, Panel } from "../components/ui";
import { useI18n } from "../i18n";
import { alarmTitle, alarmTone, alarmTypeLabel, formatAlarmTime, parseStatusFilter, validDay } from "../lib/alarms";
import { descendantIds } from "../lib/fleet";
import { navigate, type Route } from "../router/hash";
import { useAlarmCount } from "../state/alarms";
import { useSession, type TenantData } from "../state/session";
import { AlarmBadges, AlarmStatusBadge } from "./AlarmParts";

/** Newest alarms fetched per query; older ones are reached by narrowing the date range. */
export const ALARM_LIST_LIMIT = 500;

function siteOptions(tree: SiteNode[], depth = 0, out: Array<{ id: string; label: string }> = []) {
  for (const n of tree) {
    out.push({ id: n.id, label: `${"   ".repeat(depth)}${n.name}` });
    siteOptions(n.children, depth + 1, out);
  }
  return out;
}

export function operatorName(data: TenantData, operatorId: string | null): string | null {
  if (!operatorId) return null;
  const o = data.operators.operators.find((x) => x.id === operatorId);
  // Anonymised (departed) operators keep their id; their name shows as "—" (ADR 0011, KVKK).
  return o && !o.anonymized ? o.fullName : null;
}

export function AlarmsList({ data, route }: { data: TenantData; route: Route }) {
  const { t, locale } = useI18n();
  const { provider, tenant } = useSession();
  const { version } = useAlarmCount();
  const status = parseStatusFilter(route.query.get("status"));
  const typeParam = route.query.get("type");
  const type = typeParam && (ALARM_TYPES as readonly string[]).includes(typeParam) ? (typeParam as Alarm["type"]) : null;
  const site = route.query.get("site");
  const siteKnown = site && data.sites.some((s) => s.id === site) ? site : null;
  const from = validDay(route.query.get("from"));
  const to = validDay(route.query.get("to"));
  const dateError = !!(from && to && to < from);
  const [state, setState] = useState<{ alarms: Alarm[]; loaded: boolean; error: boolean }>({ alarms: [], loaded: false, error: false });
  const options = useMemo(() => siteOptions(buildSiteTree(data.sites, [])), [data.sites]);
  const tenantId = tenant?.id;

  useEffect(() => {
    if (!tenantId || dateError) return;
    let cancelled = false;
    const range = localDayRange(from, to);
    provider
      .listAlarms(tenantId, {
        status: status === "all" ? null : status,
        type,
        siteIds: siteKnown ? [...descendantIds(data.sites, siteKnown)] : null,
        from: range.from,
        to: range.to,
        limit: ALARM_LIST_LIMIT,
      })
      .then((alarms) => !cancelled && setState({ alarms, loaded: true, error: false }))
      .catch(() => !cancelled && setState((s) => ({ ...s, loaded: true, error: true })));
    return () => {
      cancelled = true;
    };
  }, [provider, tenantId, status, type, siteKnown, from, to, dateError, data.sites, version]);

  const update = (patch: Record<string, string | null>) => {
    const next = { status: status === "open" ? null : status, type, site: siteKnown, from, to, ...patch };
    navigate("/alarms", {
      status: next.status || undefined,
      type: next.type || undefined,
      site: next.site || undefined,
      from: next.from || undefined,
      to: next.to || undefined,
    });
  };
  const hasFilter = status !== "open" || !!(type || siteKnown || from || to);
  const siteName = (id: string | null) => (id ? data.sites.find((s) => s.id === id)?.name : undefined) ?? t("common.empty");
  const vehicle = (id: string | null) => (id ? data.vehicles.find((v) => v.id === id) : undefined);

  return (
    <>
      <PageHeader title={t("alarms.title")} subtitle={t("alarms.subtitle")} />
      <div className="filters" role="group" aria-label={t("alarms.filtersLabel")}>
        <div className="field field-inline">
          <label htmlFor="f-astatus">{t("alarms.filter.status")}</label>
          <select id="f-astatus" value={status} onChange={(e) => update({ status: e.target.value === "open" ? null : e.target.value })}>
            <option value="open">{t("alarms.filter.open")}</option>
            <option value="acknowledged">{t("alarms.filter.acknowledged")}</option>
            <option value="all">{t("alarms.filter.all")}</option>
          </select>
        </div>
        <div className="field field-inline">
          <label htmlFor="f-atype">{t("alarms.filter.type")}</label>
          <select id="f-atype" value={type ?? ""} onChange={(e) => update({ type: e.target.value || null })}>
            <option value="">{t("alarms.filter.allTypes")}</option>
            {ALARM_TYPES.map((x) => (
              <option key={x} value={x}>
                {alarmTypeLabel(t, x)}
              </option>
            ))}
          </select>
        </div>
        <div className="field field-inline">
          <label htmlFor="f-asite">{t("alarms.filter.site")}</label>
          <select id="f-asite" value={siteKnown ?? ""} onChange={(e) => update({ site: e.target.value || null })}>
            <option value="">{t("alarms.filter.allSites")}</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field field-inline">
          <label htmlFor="f-afrom">{t("alarms.filter.from")}</label>
          <input id="f-afrom" type="date" value={from ?? ""} max={to ?? undefined} onChange={(e) => update({ from: e.target.value || null })} />
        </div>
        <div className="field field-inline">
          <label htmlFor="f-ato">{t("alarms.filter.to")}</label>
          <input
            id="f-ato"
            type="date"
            value={to ?? ""}
            min={from ?? undefined}
            aria-invalid={dateError || undefined}
            aria-describedby={dateError ? "f-ato-error" : undefined}
            onChange={(e) => update({ to: e.target.value || null })}
          />
        </div>
        {hasFilter && (
          <button type="button" className="link-button" onClick={() => navigate("/alarms")}>
            {t("alarms.filter.clear")}
          </button>
        )}
      </div>
      {dateError && (
        <p id="f-ato-error" className="form-error" role="alert">
          {t("alarms.filter.dateError")}
        </p>
      )}
      {state.error && (
        <p className="form-error" role="alert">
          {t("alarms.loadError")}
        </p>
      )}
      <Panel flush meta={state.loaded ? <span>{t("alarms.shown", { count: state.alarms.length })}</span> : undefined}>
        {!state.loaded ? (
          <div className="loading" role="status">
            <span className="spinner" aria-hidden="true" />
            {t("common.loading")}
          </div>
        ) : state.alarms.length === 0 ? (
          <p className="table-empty">{status === "open" && !hasFilter ? t("alarms.emptyOpen") : t("alarms.empty")}</p>
        ) : (
          <div className="table-wrap">
            <table className="table alarm-table">
              <thead>
                <tr>
                  <th scope="col">{t("alarms.col.time")}</th>
                  <th scope="col">{t("alarms.col.type")}</th>
                  <th scope="col">{t("alarms.col.vehicle")}</th>
                  <th scope="col" className="col-hide-sm">
                    {t("alarms.col.site")}
                  </th>
                  <th scope="col" className="col-hide-md">
                    {t("alarms.col.operator")}
                  </th>
                  <th scope="col">{t("alarms.col.status")}</th>
                </tr>
              </thead>
              <tbody>
                {state.alarms.map((a) => {
                  const v = vehicle(a.vehicleId);
                  const time = formatAlarmTime(a.occurredAt, locale) ?? t("common.empty");
                  return (
                    <tr key={a.id}>
                      <td className="nowrap">
                        <a
                          href={`#/alarms/${encodeURIComponent(a.id)}`}
                          className="row-link"
                          aria-label={t("alarms.rowLink", { type: alarmTitle(t, a), time })}
                        >
                          {time}
                        </a>
                      </td>
                      <td>
                        <span className="alarm-type">
                          <span className={`status-dot tone-alarm-${alarmTone(a)}`} aria-hidden="true" />
                          {alarmTitle(t, a)}
                        </span>
                        <AlarmBadges alarm={a} />
                      </td>
                      <td>
                        {v ? (
                          <a href={`#/vehicles/${encodeURIComponent(v.id)}`} className="subtle-link mono">
                            {v.fleetNo}
                          </a>
                        ) : (
                          t("common.empty")
                        )}
                      </td>
                      <td className="col-hide-sm">{siteName(a.siteId)}</td>
                      <td className="col-hide-md">{operatorName(data, a.operatorId) ?? t("common.empty")}</td>
                      <td>
                        <AlarmStatusBadge status={a.status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {state.loaded && state.alarms.length >= ALARM_LIST_LIMIT && <p className="panel-footnote">{t("alarms.limitNote", { limit: ALARM_LIST_LIMIT })}</p>}
    </>
  );
}
